import { compact, difference, range } from "lodash";
import { ActionIcon } from "PFComponents/action_icon";
import { FilterFamily } from "PFTypes";
import { FiltersConfigTemplateElement } from "PFTypes/filters_config";
import React from "react";
import { useFieldArray, useWatch } from "react-hook-form";

import { useIsFieldRequired } from "../hooks/use_is_field_required";
import { useIsFilterReadOnly } from "../hooks/use_is_filter_read_only";
import { useValidateMutualExclusion } from "../hooks/use_validate_mutual_exclusion";
import css from "./filter.module.scss";
import { Property } from "./property";

type FilterProps = {
  filterFamily: FilterFamily | "children";
  template: FiltersConfigTemplateElement;
  index: number;
  onRemove?: (index: number) => void;
};

export const Filter = ({ filterFamily, template, index, onRemove }: FilterProps) => {
  const handleRemove = () => onRemove?.(index);
  const fieldPrefix = `${filterFamily}.${index}`;

  const isReadOnly = useIsFilterReadOnly({ fieldPrefix, template });
  const isFieldRequired = useIsFieldRequired({ fieldPrefix, template });
  const validateMutualExclusion = useValidateMutualExclusion({ fieldPrefix, template });
  const { fields } = useFieldArray({ name: filterFamily });

  const selectedNames = useWatch({
    name: compact(
      range(fields.length).map((number) => {
        if (number !== index) {
          return `${filterFamily}.${number}.name`;
        }
      })
    )
  });

  const nameOptions = difference(template.names, selectedNames.concat(template.readonly_names));
  const hasName = !["availability", "children"].includes(filterFamily);

  return (
    <div className={css.container}>
      {hasName && (
        <Property
          name="name"
          disabled={isReadOnly}
          fieldPrefix={fieldPrefix}
          options={nameOptions}
          required
          type="select_one"
        />
      )}
      {Object.keys(template.inputs).map((propertyName) => (
        <Property
          key={propertyName}
          name={propertyName}
          {...template.inputs[propertyName]}
          required={isFieldRequired(propertyName)}
          disabled={isReadOnly}
          fieldPrefix={fieldPrefix}
          validate={(value) => validateMutualExclusion(propertyName, value)}
        />
      ))}
      {onRemove && <ActionIcon classes={{ root: css.removeButton }} name="remove" onClick={handleRemove} />}
    </div>
  );
};
